// src/features/production/shortageHold/pages/ShortageHoldBoardPage.tsx

import { useState } from "react";
import { useAuthStore } from "@/auth/authStore";
import {
  useCreateShortageHold,
  useQcApproveShortageHold,
  useResumeShortageHold,
  useShortageHoldsList,
} from "../api";
import { BomOperationAllocationPicker } from "../components/BomOperationAllocationPicker";
import { LocationPicker } from "../components/LocationPicker";
import { WorkOrderPicker } from "../components/WorkOrderPicker";
import { useShortageHoldStore } from "../store/shortageHoldStore";
import type { ShortageResolutionMode } from "../types";
import { PageBanner } from "@/shared/components/PageBanner";

interface Props {
  initialCreateOpen?: boolean;
}

export function ShortageHoldBoardPage({ initialCreateOpen = false }: Props) {
  const currentUserId = useAuthStore((s) => s.user?.id);

  const selectedWorkOrderId = useShortageHoldStore((s) => s.selectedWorkOrderId);
  const selectedStatus = useShortageHoldStore((s) => s.selectedStatus);
  const setSelectedStatus = useShortageHoldStore((s) => s.setSelectedStatus);

  const { data: holds = [], isLoading } = useShortageHoldsList({
    work_order: selectedWorkOrderId ?? undefined,
    status: selectedStatus ?? undefined,
  });
  const createHold = useCreateShortageHold();
  const qcApprove = useQcApproveShortageHold();
  const resume = useResumeShortageHold();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(initialCreateOpen);
  const [workOrder, setWorkOrder] = useState("");
  const [missingAllocation, setMissingAllocation] = useState("");
  const [holdLocation, setHoldLocation] = useState("");
  const [resolutionMode, setResolutionMode] = useState<ShortageResolutionMode>("stay_in_line");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const handleWorkOrderChange = (id: string) => {
    setWorkOrder(id);
    setMissingAllocation("");
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!workOrder || !missingAllocation || !holdLocation) {
      setError("انتخاب Work Order، تخصیص کمبود و Location الزامی است.");
      return;
    }
    try {
      await createHold.mutateAsync({
        work_order: workOrder,
        missing_allocation: missingAllocation,
        hold_location: holdLocation,
        resolution_mode: resolutionMode,
        notes,
      });
      setWorkOrder("");
      setMissingAllocation("");
      setHoldLocation("");
      setNotes("");
      setIsCreateModalOpen(false);
    } catch {
      setError("ثبت انبار ناقصی انجام نشد. لطفاً ورودی‌ها را بررسی کنید.");
    }
  };

  const handleQcApprove = async (id: string) => {
    if (!currentUserId) return;
    await qcApprove.mutateAsync({ id, approved_by_id: currentUserId });
  };

  const handleResume = async (id: string) => {
    await resume.mutateAsync({ id });
  };

  const activeHolds = holds.filter(
    (h) => h.status === "awaiting_component" || (h.status === "component_available" && !h.qc_approved_at),
  );

  const filteredHolds = holds.filter((h) => {
    if (selectedStatus && h.status !== selectedStatus) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const wo = String(h.work_order || "").toLowerCase();
      const noteStr = String(h.notes || "").toLowerCase();
      const resStr = String(h.resolution_mode_display || h.resolution_mode || "").toLowerCase();
      if (!wo.includes(search) && !noteStr.includes(search) && !resStr.includes(search)) {
        return false;
      }
    }
    return true;
  });

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <PageBanner
        title="انبار ناقصی‌ها (Shortage Holds)"
        subtitle="مدیریت و پیگیری واحدهای ناقص و توقف‌های ناشی از کسر قطعه در خط تولید"
        icon="📦"
        helpLink="/help/production/shortageHold/shortage-hold-board"
      >
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
          <button
            type="button"
            onClick={() => setIsCreateModalOpen(true)}
            style={{
              padding: "0.6rem 1.2rem",
              borderRadius: "12px",
              background: "#ffffff",
              color: "#007270",
              border: "1px solid rgba(255, 255, 255, 0.8)",
              fontWeight: 700,
              fontSize: "0.9rem",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>📦</span>
            <span>ثبت انبار ناقصی جدید</span>
          </button>
        </div>
      </PageBanner>

      {error && !isCreateModalOpen && (
        <div
          style={{
            padding: "0.85rem 1.1rem",
            borderRadius: "10px",
            background: "rgba(239, 68, 68, 0.12)",
            border: "1px solid #EF4444",
            color: "#B91C1C",
            fontSize: "0.85rem",
            fontWeight: 600,
          }}
        >
          🚨 {error}
        </div>
      )}

      {/* KPI Cards / Current status summary for active holds */}
      {activeHolds.slice(0, 3).map((hold) => (
        <div
          key={hold.id}
          style={{
            padding: "1.25rem 1.5rem",
            background: hold.status === "awaiting_component" ? "rgba(245, 158, 11, 0.05)" : "rgba(2, 132, 199, 0.05)",
            border: `1px solid ${hold.status === "awaiting_component" ? "#F59E0B" : "#0284C7"}`,
            borderRadius: "14px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: `0 10px 30px ${hold.status === "awaiting_component" ? "rgba(245, 158, 11, 0.08)" : "rgba(2, 132, 199, 0.08)"}`,
            flexWrap: "wrap",
            gap: "1rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <span
              style={{
                fontSize: "1.8rem",
                background: hold.status === "awaiting_component" ? "#F59E0B" : "#0284C7",
                color: "#FFF",
                width: "48px",
                height: "48px",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {hold.status === "awaiting_component" ? "⚠️" : "📦"}
            </span>
            <div>
              <p
                style={{
                  fontWeight: 800,
                  color: hold.status === "awaiting_component" ? "#B45309" : "#0369A1",
                  fontSize: "1.05rem",
                  margin: "0 0 0.25rem 0",
                }}
              >
                سفارش کاری {hold.work_order}{" "}
                {hold.status === "awaiting_component"
                  ? "در وضعیت توقف به علت کمبود قطعه است!"
                  : "قطعه آن تأمین شد و در انتظار اقدام است!"}
              </p>
              <p style={{ color: "var(--color-text-muted, #666)", margin: 0, fontSize: "0.85rem" }}>
                زمان ورود:{" "}
                <span style={{ direction: "ltr", display: "inline-block", fontWeight: "bold" }}>
                  {new Date(hold.entered_at).toLocaleString("fa-IR")}
                </span>{" "}
                | روش انباشت: {hold.resolution_mode_display || hold.resolution_mode}
              </p>
            </div>
          </div>
          <div>
            <span
              style={{
                background:
                  hold.status === "awaiting_component"
                    ? "rgba(245, 158, 11, 0.15)"
                    : "rgba(2, 132, 199, 0.15)",
                color: hold.status === "awaiting_component" ? "#B45309" : "#0284C7",
                padding: "6px 14px",
                borderRadius: "8px",
                fontSize: "0.85rem",
                fontWeight: 700,
              }}
            >
              {hold.status === "awaiting_component" ? "در انتظار قطعه" : "قطعه موجود شد"}
            </span>
          </div>
        </div>
      ))}

      {/* CONTROL BAR */}
      <div
        className="brand-card-extand"
        style={{
          padding: "1rem 1.25rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            style={{
              padding: "6px 12px",
              borderRadius: "8px",
              border: "1px solid var(--color-border, #E4DCC8)",
              background: "#FFFFFF",
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "var(--color-text-main, #2B2B2B)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span>🖨️</span> چاپ
          </button>
          <button
            style={{
              padding: "6px 12px",
              borderRadius: "8px",
              border: "1px solid var(--color-border, #E4DCC8)",
              background: "#FFFFFF",
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "var(--color-text-main, #2B2B2B)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span>📄</span> خروجی PDF
          </button>
          <button
            style={{
              padding: "6px 12px",
              borderRadius: "8px",
              border: "1px solid var(--color-border, #E4DCC8)",
              background: "#FFFFFF",
              fontSize: "0.8rem",
              fontWeight: 700,
              color: "var(--color-text-main, #2B2B2B)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span>📊</span> خروجی Excel
          </button>
        </div>

        <div style={{ display: "flex", gap: "0.75rem", flex: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <select
            value={selectedStatus ?? ""}
            onChange={(e) => setSelectedStatus(e.target.value || null)}
            style={{
              padding: "6px 12px",
              borderRadius: "8px",
              border: "1px solid var(--color-border, #E4DCC8)",
              fontSize: "0.85rem",
              outline: "none",
              minWidth: "150px",
              background: "#FFFFFF",
              color: "#2B2B2B",
            }}
          >
            <option value="">وضعیت (همه)</option>
            <option value="awaiting_component">در انتظار قطعه</option>
            <option value="component_available">قطعه موجود شد</option>
            <option value="resumed">بازگشت به خط</option>
          </select>
          <input
            type="text"
            placeholder="جستجو (Work Order، یادداشت...)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "6px 12px",
              borderRadius: "8px",
              border: "1px solid var(--color-border, #E4DCC8)",
              fontSize: "0.85rem",
              outline: "none",
              minWidth: "200px",
              background: "#FFFFFF",
              color: "#2B2B2B",
            }}
          />
        </div>
      </div>

      {/* TABLE */}
      <div className="brand-card-extand" style={{ padding: "0", overflow: "hidden" }}>
        {isLoading ? (
          <div style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-muted)", fontWeight: 600 }}>
            در حال بارگذاری...
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
              <thead>
                <tr
                  style={{
                    background: "rgba(0,0,0,0.02)",
                    borderBottom: "1px solid var(--color-border, #E4DCC8)",
                    color: "var(--color-text-main, #2B2B2B)",
                  }}
                >
                  <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>شماره</th>
                  <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>سفارش کاری</th>
                  <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>وضعیت</th>
                  <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>روش انباشت/حل</th>
                  <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>زمان ورود</th>
                  <th style={{ padding: "1rem", textAlign: "center", fontWeight: 800 }}>رسیدن قطعه</th>
                  <th style={{ padding: "1rem", textAlign: "center", fontWeight: 800 }}>بازگشت به خط</th>
                  <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>یادداشت</th>
                  <th style={{ padding: "1rem", textAlign: "center", fontWeight: 800 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {filteredHolds.map((h, index) => (
                  <tr
                    key={h.id}
                    style={{ borderBottom: "1px solid rgba(0,0,0,0.05)", transition: "background 0.2s" }}
                    className="hover:bg-gray-50/50"
                  >
                    <td style={{ padding: "1rem", fontWeight: 700, color: "#555" }}>#{holds.length - index}</td>
                    <td style={{ padding: "1rem", fontWeight: 700, color: "#2B2B2B", fontFamily: "monospace" }}>
                      {h.work_order}
                    </td>
                    <td style={{ padding: "1rem" }}>
                      {h.status === "awaiting_component" ? (
                        <span
                          style={{
                            background: "rgba(245, 158, 11, 0.15)",
                            color: "#B45309",
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                          }}
                        >
                          در انتظار قطعه
                        </span>
                      ) : h.status === "component_available" ? (
                        <span
                          style={{
                            background: "rgba(2, 132, 199, 0.15)",
                            color: "#0284C7",
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                          }}
                        >
                          قطعه موجود شد
                        </span>
                      ) : (
                        <span
                          style={{
                            background: "rgba(16, 185, 129, 0.15)",
                            color: "#10B981",
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                          }}
                        >
                          بازگشت به خط
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "1rem", color: "#444" }}>
                      {h.resolution_mode_display ||
                        (h.resolution_mode === "stay_in_line" ? "در خط تولید می‌ماند" : "انتقال به انبار")}
                    </td>
                    <td style={{ padding: "1rem", direction: "ltr", textAlign: "right", fontSize: "0.85rem", color: "#666" }}>
                      {new Date(h.entered_at).toLocaleString("fa-IR")}
                    </td>
                    <td style={{ padding: "1rem", direction: "ltr", textAlign: "center", fontSize: "0.85rem", color: "#666" }}>
                      {h.component_arrived_at ? new Date(h.component_arrived_at).toLocaleString("fa-IR") : "---"}
                    </td>
                    <td style={{ padding: "1rem", direction: "ltr", textAlign: "center", fontSize: "0.85rem", color: "#666" }}>
                      {h.resumed_at ? new Date(h.resumed_at).toLocaleString("fa-IR") : "---"}
                    </td>
                    <td style={{ padding: "1rem", color: "#555", fontSize: "0.85rem" }}>{h.notes || "-"}</td>
                    <td style={{ padding: "1rem", textAlign: "center" }}>
                      <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
                        {h.status === "component_available" && !h.qc_approved_at && (
                          <button
                            onClick={() => handleQcApprove(h.id)}
                            disabled={qcApprove.isPending || !currentUserId}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "8px",
                              background: "#0284C7",
                              color: "#ffffff",
                              border: "none",
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              cursor: qcApprove.isPending || !currentUserId ? "not-allowed" : "pointer",
                              transition: "background 0.2s",
                            }}
                            title={!currentUserId ? "کاربر لاگین شناسایی نشد" : undefined}
                          >
                            تأیید QC
                          </button>
                        )}
                        {h.status === "component_available" && (
                          <button
                            onClick={() => handleResume(h.id)}
                            disabled={resume.isPending}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "8px",
                              background: "#10B981",
                              color: "#ffffff",
                              border: "none",
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              cursor: resume.isPending ? "not-allowed" : "pointer",
                              transition: "background 0.2s",
                            }}
                          >
                            بازگشت به خط
                          </button>
                        )}
                        {h.status !== "component_available" && "-"}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredHolds.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      style={{
                        padding: "3rem",
                        textAlign: "center",
                        color: "var(--color-text-muted)",
                        fontWeight: 600,
                      }}
                    >
                      هیچ انبار ناقصی‌ای با این فیلتر یافت نشد.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CREATE SHORTAGE HOLD MODAL */}
      {isCreateModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            className="brand-card-extand"
            style={{
              padding: "1.5rem",
              width: "100%",
              maxWidth: "540px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: "0.75rem",
                marginBottom: "1.5rem",
                borderBottom: "1px solid var(--color-border, #E4DCC8)",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>
                📦 ثبت انبار ناقصی جدید
              </h3>
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                style={{ background: "none", border: "none", color: "#888", fontSize: "1.2rem", cursor: "pointer" }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate} style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              <div>
                <label
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: "#2B2B2B",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  سفارش کاری (Work Order)
                </label>
                <WorkOrderPicker value={workOrder} onChange={handleWorkOrderChange} />
              </div>

              <div>
                <label
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: "#2B2B2B",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  تخصیص کمبود (BOM Allocation)
                </label>
                <BomOperationAllocationPicker
                  workOrderId={workOrder}
                  value={missingAllocation}
                  onChange={setMissingAllocation}
                />
              </div>

              <div>
                <label
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: "#2B2B2B",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  محل انبار ناقصی (Hold Location)
                </label>
                <LocationPicker value={holdLocation} onChange={setHoldLocation} />
              </div>

              <div>
                <label
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: "#2B2B2B",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  روش انباشت/حل (Resolution Mode)
                </label>
                <select
                  aria-label="Resolution Mode"
                  value={resolutionMode}
                  onChange={(e) => setResolutionMode(e.target.value as ShortageResolutionMode)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    border: "1px solid var(--color-border, #E4DCC8)",
                    background: "#FFFFFF",
                    color: "#2B2B2B",
                    fontSize: "0.9rem",
                    outline: "none",
                  }}
                >
                  <option value="stay_in_line">در خط تولید می‌ماند (Virtual / Stay in Line)</option>
                  <option value="physical_transfer">به انبار ناقصی‌ها منتقل می‌شود (Physical Transfer)</option>
                </select>
              </div>

              <div>
                <label
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: "#2B2B2B",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  توضیحات تکمیلی (اختیاری)
                </label>
                <input
                  placeholder="توضیحات تکمیلی یا علت کمبود..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    border: "1px solid var(--color-border, #E4DCC8)",
                    background: "#FFFFFF",
                    color: "#2B2B2B",
                    fontSize: "0.9rem",
                    outline: "none",
                  }}
                />
              </div>

              {error && (
                <div
                  style={{
                    padding: "0.85rem 1.1rem",
                    borderRadius: "10px",
                    background: "rgba(239, 68, 68, 0.12)",
                    border: "1px solid #EF4444",
                    color: "#B91C1C",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                  }}
                >
                  🚨 {error}
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "8px",
                  marginTop: "0.5rem",
                  paddingTop: "1rem",
                  borderTop: "1px solid var(--color-border, #E4DCC8)",
                }}
              >
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "10px",
                    background: "none",
                    border: "1px solid var(--color-border, #E4DCC8)",
                    color: "#555",
                    fontSize: "0.85rem",
                    cursor: "pointer",
                    fontWeight: 700,
                  }}
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  disabled={createHold.isPending}
                  style={{
                    padding: "8px 24px",
                    borderRadius: "10px",
                    background: "#007270",
                    color: "#FFF",
                    border: "none",
                    fontSize: "0.9rem",
                    fontWeight: 800,
                    cursor: createHold.isPending ? "not-allowed" : "pointer",
                  }}
                >
                  {createHold.isPending ? "در حال ثبت..." : "ثبت انبار ناقصی"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
